import React from 'react';

import Heading from '@atlaskit/heading';
import PanelErrorIcon from '@atlaskit/icon-lab/core/cross-octagon';
import PanelWarnIcon from '@atlaskit/icon-lab/core/vulnerability';
import PanelNoteIcon from '@atlaskit/icon/core/feed';
import TableIcon from '@atlaskit/icon/core/spreadsheet';
import PanelInfoIcon from '@atlaskit/icon/glyph/editor/panel';
import PresenceActiveIcon from '@atlaskit/icon/glyph/presence-active';
import { Box, Pressable, Stack, Text, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';

import { type TransformAction } from '../pm-plugins/ai-proactive/api';

const titleStyles = xcss({
	display: 'flex',
	justifyContent: 'space-between',
	alignItems: 'center',
	gap: 'space.100',
});

const iconStyles = xcss({
	borderWidth: 'border.width.outline',
	cursor: 'pointer',
});

const headingStyles = xcss({
	flex: 1,
});

const pressableStyles = xcss({
	color: 'color.text.subtle',
	fontWeight: token('font.weight.medium'),
	padding: 'space.0',
	backgroundColor: 'color.background.neutral.subtle',
	':hover': {
		textDecoration: 'underline',
	},
	':active': {
		color: 'color.text',
	},
});

const containerStyles = xcss({
	display: 'flex',
	paddingInline: 'space.100',
	paddingBlock: 'space.200',
	flexDirection: 'column',
	gap: 'space.050',
	alignSelf: 'stretch',
	':hover': {
		backgroundColor: 'elevation.surface.hovered',
	},
});

type ConversionDetails = {
	icon: React.ElementType;
	description: string;
	message: string;
};

const defaultDetails: ConversionDetails = {
	icon: PresenceActiveIcon,
	message: 'Suggestion',
	description: 'A suggestion to improve the content.',
};

const detailsByConversionType: Record<TransformAction, ConversionDetails> = {
	CONVERT_TO_TABLE: {
		icon: TableIcon,
		message: 'Convert to a table',
		description: 'Convert the selected content to a table.',
	},
	CONVERT_TO_NOTE_PANEL: {
		icon: PanelNoteIcon,
		message: 'Convert to a note panel',
		description: 'Convert the selected content to a note panel.',
	},
	CONVERT_TO_INFO_PANEL: {
		icon: PanelInfoIcon,
		message: 'Convert to a info panel',
		description: 'Convert the selected content to an info panel.',
	},
	CONVERT_TO_WARN_PANEL: {
		icon: PanelWarnIcon,
		message: 'Convert to a warn panel',
		description: 'Convert the selected content to a warn panel.',
	},
	CONVERT_TO_ERROR_PANEL: {
		icon: PanelErrorIcon,
		message: 'Convert to an error panel',
		description: 'Convert the selected content to an error panel.',
	},
};

type RecommendationItemProps = {
	id: string;
	action: TransformAction;
	onApply: () => void;
	onDismiss: () => void;
};

export const RecommendationItem = (props: RecommendationItemProps) => {
	const { action } = props;
	const { icon: Icon, description, message } = detailsByConversionType[action] ?? defaultDetails;

	return (
		<Stack xcss={containerStyles}>
			<Box xcss={titleStyles}>
				<Box xcss={iconStyles}>
					<Tooltip content={message} position="top">
						<Icon size="medium" label={message}></Icon>
					</Tooltip>
				</Box>
				<Box xcss={headingStyles}>
					<Heading size="xsmall">{message}</Heading>
				</Box>
				<PresenceActiveIcon
					size="small"
					label="unread"
					primaryColor={token('color.icon.information')}
				/>
			</Box>
			<Text>{description}</Text>
			<Box>
				<Pressable xcss={pressableStyles} onClick={props.onApply}>
					Apply
				</Pressable>{' '}
				&middot;{' '}
				<Pressable xcss={pressableStyles} onClick={props.onDismiss}>
					Dismiss
				</Pressable>
			</Box>
		</Stack>
	);
};
